import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  Binary,
  Bug,
  Cloud,
  Crosshair,
  Fingerprint,
  FlaskConical,
  Mail,
  Network,
  Radar,
  ScrollText,
  ShieldCheck,
  Siren,
} from 'lucide-react';
import type { Difficulty } from '../../api/types';

/** An original glyph per content category (replaces the tile-with-name style). */
export const CATEGORY_ICON: Record<string, LucideIcon> = {
  'soc-siem-operations': Activity,
  'detection-engineering': Radar,
  'dfir-forensics': Fingerprint,
  'log-analysis': ScrollText,
  'threat-hunting': Crosshair,
  'malware-triage': Bug,
  'reverse-engineering': Binary,
  'malware-analysis': Bug,
  'network-defense': Network,
  'endpoint-hardening': ShieldCheck,
  'incident-response': Siren,
  'cloud-defense': Cloud,
  'email-phishing-ti': Mail,
};

export function categoryIcon(slug: string | undefined): LucideIcon {
  return (slug && CATEGORY_ICON[slug]) || FlaskConical;
}

/** Human labels for the lab content categories (from labs.json `category`). */
export const CATEGORY_LABEL: Record<string, string> = {
  'soc-siem-operations': 'SOC & SIEM Operations',
  'detection-engineering': 'Detection Engineering',
  'dfir-forensics': 'DFIR & Forensics',
  'log-analysis': 'Log Analysis',
  'threat-hunting': 'Threat Hunting',
  'malware-triage': 'Malware Triage',
  'reverse-engineering': 'Reverse Engineering',
  'malware-analysis': 'Malware Analysis',
  'network-defense': 'Network Defense',
  'endpoint-hardening': 'Endpoint Hardening',
  'incident-response': 'Incident Response',
  'cloud-defense': 'Cloud Defense',
  'email-phishing-ti': 'Email, Phishing & Threat Intel',
};

export function categoryLabel(slug: string | undefined): string {
  if (!slug) return 'General';
  return CATEGORY_LABEL[slug] ?? slug.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The primary tool a lab teaches, derived from its VM role (ami_role). */
export const TOOL_LABEL: Record<string, string> = {
  'elastic-stack': 'Elastic SIEM',
  'splunk-siem': 'Splunk',
  'wazuh-manager': 'Wazuh EDR',
  'wazuh-indexer': 'Wazuh EDR',
  'ubuntu-desktop': 'Linux Toolkit',
  'windows-workstation': 'Windows',
  'velociraptor-server': 'Velociraptor',
};

export function toolLabel(amiRole: string | undefined): string {
  if (!amiRole) return 'Desktop';
  return TOOL_LABEL[amiRole] ?? 'Desktop';
}

/** Difficulty tier ordering + colour, in the easy→expert progression. */
export const DIFFICULTY_ORDER: ReadonlyArray<Difficulty> = ['beginner', 'intermediate', 'advanced', 'expert'];

export const DIFFICULTY_META: Record<Difficulty, { label: string; variant: 'success' | 'info' | 'warn' | 'danger' }> = {
  beginner: { label: 'Easy', variant: 'success' },
  intermediate: { label: 'Medium', variant: 'info' },
  advanced: { label: 'Hard', variant: 'warn' },
  expert: { label: 'Expert', variant: 'danger' },
};
